import {
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
} from 'drizzle-orm/pg-core'
import { relations } from 'drizzle-orm'
import { getBaseFields, getBaseIndexes, getIdReference } from '../common/utils'
import { addresses } from './address'
import { customers } from './customer'
import { subscriptions } from './subscription'

export const VENTILATION_PLANS_ANSWERS = ['yes', 'no', 'unknown'] as const
export type VentilationPlansAnswer = (typeof VENTILATION_PLANS_ANSWERS)[number]

export interface VentilationUnit {
  brand?: string
  model?: string
  location?: string
}

/**
 * What we know about the house's ventilation, built up from the customer's
 * pre-visit answers and corrected by technicians. One document per property,
 * always read and written whole, so it lives in a column rather than tables.
 */
export interface PropertyVentilation {
  units: VentilationUnit[]
  hasPlans?: VentilationPlansAnswer | null
  accessNotes?: string | null
  /** Staff-entered, e.g. "asiakas vaihtamassa konetta" — not on the customer form. */
  notes?: string | null
}

// Property type enum
export const propertyTypeEnum = pgEnum('property_type', [
  'detached_house',
  'semi_detached',
  'row_house',
  'apartment_building',
  'commercial_property',
])

export const properties = pgTable(
  'properties',
  {
    ...getBaseFields(),
    customerId: getIdReference(() => customers.id),
    propertyType: propertyTypeEnum().notNull(),
    constructionYear: integer(),
    // Free text — technicians need "iso koira, pidetään makuuhuoneessa", not a
    // species checkbox.
    pets: text(),
    ventilation: jsonb().$type<PropertyVentilation>(),
    notes: text(),
    // Operational commentary for staff (billing quirks, gate codes). The
    // `internal` prefix hides it from the homeowner, who can otherwise read
    // their own property.
    internalNotes: text(),
    data: jsonb(),
    addressId: getIdReference(() => addresses.id).notNull(),
  },
  (table) => [
    ...getBaseIndexes('properties', table),
    index('properties_customer_id_idx').on(table.customerId),
    index('properties_address_id_idx').on(table.addressId),
  ],
)

export const propertiesRelations = relations(properties, ({ one, many }) => ({
  customer: one(customers, {
    fields: [properties.customerId],
    references: [customers.id],
  }),
  address: one(addresses, {
    fields: [properties.addressId],
    references: [addresses.id],
  }),
  subscriptions: many(subscriptions),
}))
